import {
  useCreateLegalClientBody,
  useDeleteLegalClientBody,
  useLegalClientBodyArchive,
  useLegalClientBodyDearchive,
  useUpdateLegalClientBody,
} from 'api/requests';
import {
  LegalClientBodyType,
  LegalClientEntity,
  OnboardingStatusType,
  PermissionType,
} from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';

export const useBodyAction = (
  legalClient: LegalClientEntity | null | undefined,
  refetchLegalClient: () => void
) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const { createLegalClientBody, error: createError } =
    useCreateLegalClientBody();
  const { updateLegalClientBody, error: updateError } =
    useUpdateLegalClientBody();
  const { archive } = useLegalClientBodyArchive();
  const { dearchive, error: dearchiveError } = useLegalClientBodyDearchive();
  const { deleteLegalClientBody } = useDeleteLegalClientBody();

  const handleSuccess = async (message: string) => {
    await refetchLegalClient();
    notify.info(message);
    if (
      legalClient?.onboardingStatus === OnboardingStatusType.AccountOpened &&
      legalClient.isBrightSyncEnabled
    ) {
      notify.info(t('client.client.statuses.updateBrightText'));
    }
  };

  const onAddBody = checkPermissions([
    PermissionType.LegalClientChildEntity_CreateOne,
  ])
    ? async (
        body: { name: string; type: LegalClientBodyType; otherType?: string },
        reason: string
      ) => {
        await createLegalClientBody({
          name: body.name,
          legalClientId: legalClient!.id,
          reason,
          type: body.type,
          versionNumber: legalClient!.versionNumber!,
          otherType: body.otherType,
        });
        await handleSuccess(t('client.legal.companyStructure.addedMessage'));
      }
    : undefined;

  const onUpdateBody = async (
    id: string,
    body: { name: string; type: LegalClientBodyType; otherType?: string },
    reason: string
  ) => {
    await updateLegalClientBody(id, {
      name: body.name,
      reason,
      type: body.type,
      versionNumber: legalClient!.versionNumber!,
      otherType: body.otherType,
    });
    await handleSuccess(t('client.legal.companyStructure.updatedMessage'));
  };

  const onArchiveBody = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await archive(id, {
          reason,
          versionNumber: legalClient!.versionNumber!,
        });
        await handleSuccess(t('common.archivedMessage'));
      }
    : undefined;

  const onDearchiveBody = checkPermissions([
    PermissionType.LegalClientChildEntity_ArchiveOne,
  ])
    ? async (id: string, reason: string) => {
        await dearchive(id, {
          reason,
          versionNumber: legalClient!.versionNumber!,
        });
        await handleSuccess(t('common.dearchivedMessage'));
      }
    : undefined;

  const onDeleteBody = checkPermissions([
    PermissionType.LegalClientBody_DeleteOne,
  ])
    ? async (id: string, reason: string) => {
        await deleteLegalClientBody({
          id,
          reason,
          versionNumber: legalClient!.versionNumber!,
        });
        await handleSuccess(t('client.legal.companyStructure.deletedMessage'));
      }
    : undefined;

  useServerErrorNotify(updateError ?? createError ?? dearchiveError);

  return {
    onAddBody,
    onUpdateBody,
    onArchiveBody,
    onDearchiveBody,
    onDeleteBody,
  };
};
